import { useEffect, useRef, useState } from "react";
import { Menu, X, ChevronRight } from "lucide-react";
import MedicalHero3D from "./MedicalHero3D";
import MedicalIcon from "./MedicalIcons";
import {
  HOSPITAL as BASE_HOSPITAL,
  NAV_LINKS,
  ADVANTAGES,
  DEPARTMENTS,
  DOCTORS,
  BOOKING_STEPS,
  CHECKUP_PACKAGES,
  FACILITIES,
  HOURS,
  DEPT_OPTIONS,
  STATS,
  QUICK_SERVICES,
  NOTICES,
  HEALTH_ARTICLES,
  FAQ_ITEMS,
  NON_COVERED_ITEMS,
  TREATMENT_GUIDE,
  PATIENT_TABS,
  TRANSPORT,
  LEGAL_LINKS,
} from "./hiMedicalData";
import { getHospitalProfile } from "./hospitalProfiles";
import "./HiMedicalSite.css";

function Reveal({ children, className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          el.classList.add("is-visible");
          obs.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className={`hi-reveal ${className}`.trim()}>
      {children}
    </div>
  );
}

function SiteModal({ title, message, onClose, children }) {
  return (
    <div className="hi-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="hi-modal" onClick={(e) => e.stopPropagation()}>
        <div className="hi-modal__icon">
          <MedicalIcon name="checkCircle" size={28} />
        </div>
        <h3>{title}</h3>
        <p>{message}</p>
        {children}
        <button type="button" className="hi-btn hi-btn--primary" onClick={onClose}>
          확인
        </button>
      </div>
    </div>
  );
}

function FaqItem({ item, open, onToggle }) {
  return (
    <div className={`hi-faq__item${open ? " is-open" : ""}`}>
      <button type="button" className="hi-faq__q" onClick={onToggle} aria-expanded={open}>
        <span>{item.q}</span>
        <ChevronRight size={16} className="hi-faq__chev" />
      </button>
      {open && <div className="hi-faq__a">{item.a}</div>}
    </div>
  );
}

function PatientServicePanel({ activeTab }) {
  const [lookupForm, setLookupForm] = useState({ name: "", phone: "" });
  const [questionnaire, setQuestionnaire] = useState({
    symptom: "",
    duration: "",
    medication: "",
    allergy: "",
  });
  const [checkupForm, setCheckupForm] = useState({ name: "", birth: "", phone: "" });
  const [inquiryForm, setInquiryForm] = useState({ name: "", phone: "", subject: "", body: "" });
  const [panelMsg, setPanelMsg] = useState("");

  const showMsg = (msg) => {
    setPanelMsg(msg);
    setTimeout(() => setPanelMsg(""), 4000);
  };

  if (activeTab === "lookup") {
    return (
      <div className="hi-patient-panel">
        <h4>예약 조회</h4>
        <p>예약 시 등록한 이름과 연락처로 조회합니다.</p>
        <div className="hi-patient-panel__form">
          <div>
            <label htmlFor="lookup-name">이름</label>
            <input
              id="lookup-name"
              type="text"
              placeholder="홍길동"
              value={lookupForm.name}
              onChange={(e) => setLookupForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="lookup-phone">연락처</label>
            <input
              id="lookup-phone"
              type="tel"
              placeholder="010-0000-0000"
              value={lookupForm.phone}
              onChange={(e) => setLookupForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </div>
          <button
            type="button"
            className="hi-btn hi-btn--primary"
            onClick={() => {
              if (!lookupForm.name || !lookupForm.phone) return;
              showMsg("2026.03.15 (토) 10:30 · 내과 · 예약 확정");
            }}
          >
            조회하기
          </button>
        </div>
        {panelMsg && (
          <div className="hi-patient-panel__result">
            <strong>예약 정보</strong>
            <span>{panelMsg}</span>
          </div>
        )}
      </div>
    );
  }

  if (activeTab === "questionnaire") {
    return (
      <div className="hi-patient-panel">
        <h4>사전 문진 작성</h4>
        <p>방문 전 문진을 작성하면 접수·대기 시간이 단축됩니다.</p>
        <div className="hi-patient-panel__form">
          <div>
            <label htmlFor="q-symptom">주요 증상</label>
            <input
              id="q-symptom"
              type="text"
              placeholder="예: 복통, 두통"
              value={questionnaire.symptom}
              onChange={(e) => setQuestionnaire((f) => ({ ...f, symptom: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="q-duration">증상 기간</label>
            <select
              id="q-duration"
              value={questionnaire.duration}
              onChange={(e) => setQuestionnaire((f) => ({ ...f, duration: e.target.value }))}
            >
              <option value="">선택</option>
              <option value="1d">1일 이내</option>
              <option value="1w">1주 이내</option>
              <option value="1m">1개월 이상</option>
            </select>
          </div>
          <div>
            <label htmlFor="q-med">복용 중인 약</label>
            <input
              id="q-med"
              type="text"
              placeholder="없음 / 약명 입력"
              value={questionnaire.medication}
              onChange={(e) => setQuestionnaire((f) => ({ ...f, medication: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="q-allergy">알레르기</label>
            <input
              id="q-allergy"
              type="text"
              placeholder="없음 / 알레르기 입력"
              value={questionnaire.allergy}
              onChange={(e) => setQuestionnaire((f) => ({ ...f, allergy: e.target.value }))}
            />
          </div>
          <button
            type="button"
            className="hi-btn hi-btn--mint"
            onClick={() => showMsg("사전 문진이 저장되었습니다. 내원 시 접수가 간소화됩니다.")}
          >
            문진 저장
          </button>
        </div>
        {panelMsg && <div className="hi-patient-panel__result"><span>{panelMsg}</span></div>}
      </div>
    );
  }

  if (activeTab === "checkup-result") {
    return (
      <div className="hi-patient-panel">
        <h4>검진 결과 조회</h4>
        <p>검진 완료 후 안내받은 정보로 결과 조회 일정을 확인합니다.</p>
        <div className="hi-patient-panel__form">
          <div>
            <label htmlFor="cr-name">이름</label>
            <input
              id="cr-name"
              type="text"
              value={checkupForm.name}
              onChange={(e) => setCheckupForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="cr-birth">생년월일</label>
            <input
              id="cr-birth"
              type="text"
              placeholder="900101"
              value={checkupForm.birth}
              onChange={(e) => setCheckupForm((f) => ({ ...f, birth: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="cr-phone">연락처</label>
            <input
              id="cr-phone"
              type="tel"
              value={checkupForm.phone}
              onChange={(e) => setCheckupForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </div>
          <button
            type="button"
            className="hi-btn hi-btn--primary"
            onClick={() => showMsg("결과 상담 예정: 2026.03.20 (목) 14:00 · 대면 상담")}
          >
            조회하기
          </button>
        </div>
        {panelMsg && (
          <div className="hi-patient-panel__result">
            <strong>검진 결과 안내</strong>
            <span>{panelMsg}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="hi-patient-panel">
      <h4>1:1 문의</h4>
      <p>진료·검진·비용 관련 문의를 남겨 주시면 순차적으로 답변드립니다.</p>
      <div className="hi-patient-panel__form">
        <div className="hi-form-row">
          <div>
            <label htmlFor="iq-name">이름</label>
            <input
              id="iq-name"
              type="text"
              value={inquiryForm.name}
              onChange={(e) => setInquiryForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="iq-phone">연락처</label>
            <input
              id="iq-phone"
              type="tel"
              value={inquiryForm.phone}
              onChange={(e) => setInquiryForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </div>
        </div>
        <div>
          <label htmlFor="iq-subject">문의 유형</label>
          <select
            id="iq-subject"
            value={inquiryForm.subject}
            onChange={(e) => setInquiryForm((f) => ({ ...f, subject: e.target.value }))}
          >
            <option value="">선택</option>
            <option value="진료">진료 문의</option>
            <option value="검진">검진 문의</option>
            <option value="비용">비용 문의</option>
            <option value="기타">기타</option>
          </select>
        </div>
        <div>
          <label htmlFor="iq-body">문의 내용</label>
          <textarea
            id="iq-body"
            value={inquiryForm.body}
            onChange={(e) => setInquiryForm((f) => ({ ...f, body: e.target.value }))}
          />
        </div>
        <button
          type="button"
          className="hi-btn hi-btn--primary"
          onClick={() => showMsg("문의가 접수되었습니다. 영업일 기준 1~2일 내 답변드립니다.")}
        >
          문의 접수
        </button>
      </div>
      {panelMsg && <div className="hi-patient-panel__result"><span>{panelMsg}</span></div>}
    </div>
  );
}

const LEGAL_CONTENT = {
  privacy: "서울하이메디컬센터는 환자 개인정보를 진료·예약 목적으로만 수집·이용하며, 관련 법령에 따라 안전하게 관리합니다.",
  terms: "본 웹사이트의 예약·문의 서비스는 안내 목적이며, 실제 진료는 내원 후 의료진 상담을 통해 확정됩니다.",
  "non-covered": "비급여 항목은 홈페이지 비급여 안내표를 참고해 주시며, 상담 시 정확한 비용을 안내해 드립니다.",
  rights: "환자는 자신의 건강 상태에 대한 충분한 설명을 들을 권리가 있으며, 진료 거부·비밀 보장을 보장받습니다.",
};

export default function HiMedicalSite({ embed = false, profileId = "hi-medical" }) {
  const profile = getHospitalProfile(profileId);
  const hospital = { ...BASE_HOSPITAL, ...profile.hospital };
  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const [legalModal, setLegalModal] = useState(null);
  const [faqOpen, setFaqOpen] = useState(0);
  const [patientTab, setPatientTab] = useState("lookup");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    dept: "",
    date: "",
    time: "",
    visitType: "초진",
    message: "",
    agree: false,
  });

  const scrollTo = (id) => {
    setMenuOpen(false);
    if (id.startsWith("tel:")) {
      window.location.href = id;
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setModal({
      title: "예약 신청이 접수되었습니다",
      message: "담당자가 확인 후 연락드리겠습니다. 감사합니다.",
    });
    setForm({
      name: "",
      phone: "",
      dept: "",
      date: "",
      time: "",
      visitType: "초진",
      message: "",
      agree: false,
    });
  };

  return (
    <div
      className={`hi-site${embed ? " hi-site--embed" : ""}`}
      data-testid="portfolio-demo-hospital"
      style={{
        "--hi-navy": profile.accent,
        "--hi-navy-deep": profile.accentDeep,
        "--hi-blue": profile.accentBlue,
        "--hi-mint": profile.accentMint,
      }}
    >
      <div className="hi-topbar">
        <div className="hi-topbar__inner">
          <span className="hi-topbar__hours">
            <MedicalIcon name="clock" size={14} />
            평일 09:00-19:00 · 토 09:00-14:00
          </span>
          <span className="hi-topbar__phone">
            <MedicalIcon name="phone" size={14} />
            {hospital.phone}
          </span>
          <span className="hi-topbar__note">{hospital.emergencyNote}</span>
        </div>
      </div>

      <header className="hi-header">
        <div className="hi-header__inner">
          <a href="#hi-top" className="hi-brand" onClick={(e) => { e.preventDefault(); scrollTo("hi-top"); }}>
            <span className="hi-brand__name">{hospital.name}</span>
            <span className="hi-brand__en">{hospital.nameEn}</span>
          </a>
          <nav className="hi-nav" aria-label="주요 메뉴">
            {NAV_LINKS.map((l) => (
              <a key={l.id} href={`#${l.id}`} onClick={(e) => { e.preventDefault(); scrollTo(l.id); }}>
                {l.label}
              </a>
            ))}
          </nav>
          <a
            href="#booking"
            className="hi-header__cta"
            onClick={(e) => { e.preventDefault(); scrollTo("booking"); }}
          >
            진료 예약
          </a>
          <button type="button" className="hi-menu-btn" aria-label="메뉴" onClick={() => setMenuOpen((v) => !v)}>
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {menuOpen && (
          <nav className="hi-mobile-nav" aria-label="모바일 메뉴">
            {NAV_LINKS.map((l) => (
              <a key={l.id} href={`#${l.id}`} onClick={(e) => { e.preventDefault(); scrollTo(l.id); }}>
                {l.label}
              </a>
            ))}
          </nav>
        )}
      </header>

      <div className="hi-quickbar">
        <div className="hi-quickbar__inner">
          {QUICK_SERVICES.map((s) => (
            <button
              key={s.id}
              type="button"
              className="hi-quickbar__item"
              onClick={() => scrollTo(s.target)}
            >
              <MedicalIcon name={s.icon} size={20} />
              <span>{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      <section id="hi-top" className="hi-hero">
        <div className="hi-hero__grid">
          <div>
            <span className="hi-hero__badge">{hospital.nameEn}</span>
            <h1>{hospital.slogan}</h1>
            <p className="hi-hero__intro">{hospital.intro}</p>
            <div className="hi-hero__btns">
              <button type="button" className="hi-btn hi-btn--primary" onClick={() => scrollTo("booking")}>
                진료 예약하기
              </button>
              <button type="button" className="hi-btn hi-btn--outline" onClick={() => scrollTo("departments")}>
                진료과목 보기
              </button>
            </div>
            <div className="hi-stats">
              {STATS.map((s) => (
                <div key={s.label} className="hi-stat">
                  <strong>
                    {s.value}
                    <small>{s.unit}</small>
                  </strong>
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
          <MedicalHero3D />
        </div>
      </section>

      <section id="about" className="hi-section hi-section--white">
        <div className="hi-wrap">
          <Reveal>
            <div className="hi-section__head">
              <span className="hi-section__eyebrow">Why Seoul Hi</span>
              <h2>병원 핵심 장점</h2>
              <p>환자 중심의 진료 환경과 디지털 프로세스로 더 편리한 의료 경험을 제공합니다.</p>
            </div>
          </Reveal>
          <div className="hi-grid4">
            {ADVANTAGES.map((a) => (
              <Reveal key={a.title}>
                <article className="hi-card">
                  <div className="hi-card__icon">
                    <MedicalIcon name={a.icon} size={22} />
                  </div>
                  <h3>{a.title}</h3>
                  <p>{a.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="hi-guide">
              {TREATMENT_GUIDE.map((g) => (
                <article key={g.title} className="hi-guide__card">
                  <div className="hi-guide__icon">
                    <MedicalIcon name={g.icon} size={20} />
                  </div>
                  <h4>{g.title}</h4>
                  <ul>
                    {g.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section id="departments" className="hi-section hi-section--ivory">
        <div className="hi-wrap">
          <Reveal>
            <div className="hi-section__head">
              <span className="hi-section__eyebrow">Departments</span>
              <h2>진료과목 안내</h2>
              <p>분야별 전문 의료진이 맞춤 진료와 체계적인 케어를 제공합니다.</p>
            </div>
          </Reveal>
          <div className="hi-grid4">
            {DEPARTMENTS.map((d) => (
              <Reveal key={d.id}>
                <article className="hi-card hi-card--dept">
                  <div className="hi-card__icon">
                    <MedicalIcon name={d.icon} size={22} />
                  </div>
                  <h3>{d.name}</h3>
                  <p>{d.desc}</p>
                  <div className="hi-card__hours">
                    <MedicalIcon name="clock" size={14} />
                    {d.hours}
                  </div>
                  <ul className="hi-card__list">
                    {d.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <button type="button" className="hi-card__link" onClick={() => scrollTo("booking")}>
                    예약하기
                    <ChevronRight size={14} />
                  </button>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="doctors" className="hi-section hi-section--white">
        <div className="hi-wrap">
          <Reveal>
            <div className="hi-section__head">
              <span className="hi-section__eyebrow">Medical Team</span>
              <h2>의료진 소개</h2>
              <p>풍부한 임상 경험을 갖춘 전문의가 직접 진료합니다.</p>
            </div>
          </Reveal>
          <div className="hi-grid4">
            {DOCTORS.map((doc) => (
              <Reveal key={doc.name}>
                <article className="hi-card hi-doctor">
                  <div className="hi-doctor__avatar">{doc.initials}</div>
                  <h4>{doc.name}</h4>
                  <div className="hi-doctor__role">{doc.role}</div>
                  <p className="hi-doctor__focus">{doc.focus}</p>
                  <div className="hi-doctor__schedule">
                    <MedicalIcon name="calendar" size={13} />
                    {doc.schedule}
                  </div>
                  <p className="hi-doctor__career">{doc.career}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="patient" className="hi-section hi-section--ivory">
        <div className="hi-wrap">
          <Reveal>
            <div className="hi-section__head">
              <span className="hi-section__eyebrow">Patient Services</span>
              <h2>환자 서비스</h2>
              <p>예약 조회, 사전 문진, 검진 결과 확인, 1:1 문의를 온라인에서 이용하세요.</p>
            </div>
          </Reveal>
          <Reveal>
            <div className="hi-patient">
              <div className="hi-patient__tabs" role="tablist">
                {PATIENT_TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={patientTab === t.id}
                    className={`hi-patient__tab${patientTab === t.id ? " is-active" : ""}`}
                    onClick={() => setPatientTab(t.id)}
                  >
                    <MedicalIcon name={t.icon} size={16} />
                    {t.label}
                  </button>
                ))}
              </div>
              <PatientServicePanel activeTab={patientTab} />
            </div>
          </Reveal>

          <Reveal>
            <div className="hi-steps hi-steps--spaced">
              <div className="hi-section__head" style={{ marginBottom: "1.5rem" }}>
                <span className="hi-section__eyebrow">Smart Booking</span>
                <h2>스마트 예약 프로세스</h2>
              </div>
              <div className="hi-steps__grid">
                {BOOKING_STEPS.map((s) => (
                  <div key={s.step} className="hi-step">
                    <div className="hi-step__num">{s.step}</div>
                    <h4>{s.title}</h4>
                    <p>{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="checkup" className="hi-section hi-section--white">
        <div className="hi-wrap">
          <Reveal>
            <div className="hi-section__head">
              <span className="hi-section__eyebrow">Health Check-up</span>
              <h2>건강검진 패키지</h2>
              <p>연령과 건강 상태에 맞는 검진 프로그램을 선택하세요.</p>
            </div>
          </Reveal>
          <div className="hi-packages">
            {CHECKUP_PACKAGES.map((pkg) => (
              <Reveal key={pkg.name}>
                <article className={`hi-package${pkg.featured ? " hi-package--featured" : ""}`}>
                  {pkg.featured && <span className="hi-package__badge">추천</span>}
                  <h3>{pkg.name}</h3>
                  <div className="hi-package__price">{pkg.price}</div>
                  <p className="hi-package__desc">{pkg.desc}</p>
                  <ul>
                    {pkg.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <button type="button" className="hi-btn hi-btn--outline hi-package__btn" onClick={() => scrollTo("booking")}>
                    검진 예약
                  </button>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="hi-section hi-section--ivory">
        <div className="hi-wrap">
          <Reveal>
            <div className="hi-section__head">
              <span className="hi-section__eyebrow">Facilities</span>
              <h2>병원 시설</h2>
              <p>쾌적하고 프라이버시가 보장되는 진료·검진 환경을 갖추고 있습니다.</p>
            </div>
          </Reveal>
          <div className="hi-facilities">
            {FACILITIES.map((f) => (
              <Reveal key={f.name}>
                <article>
                  <div className="hi-facility__img" style={{ background: f.tone }} />
                  <div className="hi-facility__body">
                    <h4>{f.name}</h4>
                    <p>{f.desc}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="notice" className="hi-section hi-section--white">
        <div className="hi-wrap">
          <Reveal>
            <div className="hi-section__head">
              <span className="hi-section__eyebrow">Notice & Guide</span>
              <h2>공지·안내</h2>
              <p>병원 소식, 진료 안내, 건강 정보를 확인하세요.</p>
            </div>
          </Reveal>

          <div className="hi-notice-grid">
            <Reveal>
              <div className="hi-notice-board">
                <div className="hi-notice-board__head">
                  <h3>공지사항</h3>
                  <button type="button" className="hi-text-btn">전체보기</button>
                </div>
                <ul>
                  {NOTICES.map((n) => (
                    <li key={n.id}>
                      <button type="button" className="hi-notice-row">
                        <span className={`hi-notice-cat${n.pinned ? " hi-notice-cat--pin" : ""}`}>
                          {n.pinned ? "필독" : n.category}
                        </span>
                        <span className="hi-notice-title">{n.title}</span>
                        <span className="hi-notice-date">{n.date}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal>
              <div className="hi-articles">
                <div className="hi-notice-board__head">
                  <h3>건강 정보</h3>
                </div>
                {HEALTH_ARTICLES.map((a) => (
                  <article key={a.title} className="hi-article">
                    <span className="hi-article__cat">{a.category}</span>
                    <h4>{a.title}</h4>
                    <p>{a.summary}</p>
                    <span className="hi-article__date">{a.date}</span>
                  </article>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal>
            <div className="hi-faq">
              <h3>자주 묻는 질문</h3>
              {FAQ_ITEMS.map((item, i) => (
                <FaqItem
                  key={item.q}
                  item={item}
                  open={faqOpen === i}
                  onToggle={() => setFaqOpen(faqOpen === i ? -1 : i)}
                />
              ))}
            </div>
          </Reveal>

          <Reveal>
            <div className="hi-noncovered">
              <h3>비급여 진료비 안내</h3>
              <p className="hi-noncovered__note">표시 금액은 참고용이며, 상담 시 정확한 비용을 안내해 드립니다.</p>
              <table>
                <thead>
                  <tr>
                    <th>항목</th>
                    <th>비용</th>
                    <th>구분</th>
                  </tr>
                </thead>
                <tbody>
                  {NON_COVERED_ITEMS.map((row) => (
                    <tr key={row.name}>
                      <td>{row.name}</td>
                      <td>{row.price}</td>
                      <td><span className="hi-tag">{row.note}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="booking" className="hi-section hi-section--ivory">
        <div className="hi-wrap">
          <Reveal>
            <div className="hi-section__head">
              <span className="hi-section__eyebrow">Reservation</span>
              <h2>진료 예약</h2>
              <p>아래 양식을 작성해 주시면 담당자가 확인 후 연락드립니다.</p>
            </div>
          </Reveal>
          <div className="hi-booking">
            <Reveal>
              <div className="hi-booking-info">
                <h3>예약 안내</h3>
                <p>
                  온라인 예약 접수 후 영업시간 내 순차적으로 확인하여 연락드립니다.
                  긴급 상담은 전화 {hospital.phone}으로 문의해 주세요.
                </p>
                <ul className="hi-hours">
                  {HOURS.map((h) => (
                    <li key={h.label}>
                      <span>{h.label}</span>
                      <span>{h.time}</span>
                    </li>
                  ))}
                </ul>
                <div className="hi-booking-info__call">
                  <MedicalIcon name="phone" size={18} />
                  <div>
                    <strong>예약 전화</strong>
                    <span>{hospital.phone}</span>
                  </div>
                </div>
              </div>
            </Reveal>
            <Reveal>
              <form className="hi-form" onSubmit={handleSubmit}>
                <div className="hi-form-row">
                  <div>
                    <label htmlFor="hi-name">이름</label>
                    <input
                      id="hi-name"
                      type="text"
                      placeholder="홍길동"
                      required
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label htmlFor="hi-phone">연락처</label>
                    <input
                      id="hi-phone"
                      type="tel"
                      placeholder="010-0000-0000"
                      required
                      value={form.phone}
                      onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="hi-form-row">
                  <div>
                    <label htmlFor="hi-dept">진료과목</label>
                    <select
                      id="hi-dept"
                      required
                      value={form.dept}
                      onChange={(e) => setForm((f) => ({ ...f, dept: e.target.value }))}
                    >
                      <option value="">선택해 주세요</option>
                      {DEPT_OPTIONS.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="hi-visit">초진/재진</label>
                    <select
                      id="hi-visit"
                      value={form.visitType}
                      onChange={(e) => setForm((f) => ({ ...f, visitType: e.target.value }))}
                    >
                      <option value="초진">초진</option>
                      <option value="재진">재진</option>
                    </select>
                  </div>
                </div>
                <div className="hi-form-row">
                  <div>
                    <label htmlFor="hi-date">희망 날짜</label>
                    <input
                      id="hi-date"
                      type="date"
                      required
                      value={form.date}
                      onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label htmlFor="hi-time">희망 시간</label>
                    <select
                      id="hi-time"
                      required
                      value={form.time}
                      onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                    >
                      <option value="">선택</option>
                      <option value="09:00">09:00</option>
                      <option value="10:00">10:00</option>
                      <option value="11:00">11:00</option>
                      <option value="14:00">14:00</option>
                      <option value="15:00">15:00</option>
                      <option value="16:00">16:00</option>
                      <option value="17:00">17:00</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="hi-msg">문의 내용</label>
                  <textarea
                    id="hi-msg"
                    placeholder="증상이나 희망 진료 시간을 적어 주세요."
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  />
                </div>
                <label className="hi-checkbox">
                  <input
                    type="checkbox"
                    required
                    checked={form.agree}
                    onChange={(e) => setForm((f) => ({ ...f, agree: e.target.checked }))}
                  />
                  <span>개인정보 수집·이용에 동의합니다.</span>
                </label>
                <button type="submit" className="hi-btn hi-btn--mint" style={{ width: "100%" }}>
                  예약 신청
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="location" className="hi-section hi-section--white">
        <div className="hi-wrap">
          <Reveal>
            <div className="hi-section__head">
              <span className="hi-section__eyebrow">Location</span>
              <h2>오시는 길</h2>
            </div>
          </Reveal>
          <div className="hi-location">
            <Reveal>
              <div className="hi-location-info">
                <p className="hi-location-info__addr">{hospital.address}</p>
                <ul className="hi-hours">
                  {HOURS.map((h) => (
                    <li key={h.label}>
                      <span>{h.label}</span>
                      <span>{h.time}</span>
                    </li>
                  ))}
                </ul>
                <ul className="hi-transport">
                  {TRANSPORT.map((t) => (
                    <li key={t.type}>
                      <strong>{t.type}</strong>
                      <span>{t.info}</span>
                    </li>
                  ))}
                </ul>
                <p className="hi-location-info__contact">
                  문의: {hospital.phone} · {hospital.email}
                </p>
              </div>
            </Reveal>
            <Reveal>
              <div className="hi-map">
                <div className="hi-map__marker">
                  <MedicalIcon name="mapPin" size={28} />
                </div>
                <strong>서울하이메디컬센터</strong>
                <span>강남구 테헤란로 128</span>
                <span className="hi-map__sub">지도 영역 (Placeholder)</span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <footer className="hi-footer">
        <div className="hi-footer__grid">
          <div className="hi-footer__brand">
            <strong>{hospital.name}</strong>
            <p>
              {hospital.slogan}
              <br />
              {hospital.address}
            </p>
            <p className="hi-footer__biz">
              대표: {hospital.ceo} · 사업자등록번호 {hospital.bizNo}
              <br />
              Tel {hospital.phone} · Fax {hospital.fax}
            </p>
          </div>
          <div className="hi-footer__col">
            <strong>진료과목</strong>
            {DEPARTMENTS.map((d) => (
              <span key={d.id}>{d.name}</span>
            ))}
          </div>
          <div className="hi-footer__col">
            <strong>바로가기</strong>
            {LEGAL_LINKS.map((l) => (
              <button
                key={l.id}
                type="button"
                className="hi-footer__link"
                onClick={() => setLegalModal(l.id)}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
        <div className="hi-footer__copy">
          © 2026 {hospital.nameEn}. All rights reserved.
          <div className="hi-footer__ds">Portfolio by DSiteSolution — 병원 홈페이지 제작 사례</div>
        </div>
      </footer>

      <div className="hi-floating">
        <button type="button" className="hi-floating__btn" onClick={() => scrollTo("booking")}>
          <MedicalIcon name="calendar" size={18} />
          예약
        </button>
        <a href={`tel:${hospital.phone}`} className="hi-floating__btn hi-floating__btn--phone">
          <MedicalIcon name="phone" size={18} />
          전화
        </a>
      </div>

      {modal && (
        <SiteModal title={modal.title} message={modal.message} onClose={() => setModal(null)} />
      )}

      {legalModal && (
        <SiteModal
          title={LEGAL_LINKS.find((l) => l.id === legalModal)?.label || "안내"}
          message={LEGAL_CONTENT[legalModal]}
          onClose={() => setLegalModal(null)}
        />
      )}
    </div>
  );
}
